/**
 * BreakGlassModal — the VAL-3 duty-to-warn override. Used when no publication consent exists: a
 * critical FACT reaches the prescriber anyway. A trimmed justification is MANDATORY (the server
 * refuses without one); the scope is the critical fact only (never the full dossier); the record
 * lands `reviewed=false` for mandatory post-hoc review. Gated by the caller on
 * `validation.valeurcritique.acknowledge`. Confirmation requires an explicit action — not dismissable
 * by overlay-click while submitting.
 */
import { useEffect, useState } from "react";
import { Button, FormField, Input, Modal, Textarea } from "@/ui";
import { useT } from "@/app";
import { token } from "@/design/theme";
import type { Id } from "../types";

export interface BreakGlassModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (input: { justification: string; medecin_ref?: Id }) => Promise<void>;
  submitting?: boolean;
}

export function BreakGlassModal({ open, onClose, onConfirm, submitting }: BreakGlassModalProps) {
  const { t } = useT();
  const [justification, setJustification] = useState("");
  const [medecin, setMedecin] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setJustification("");
      setMedecin("");
      setTouched(false);
    }
  }, [open]);

  const invalid = justification.trim().length === 0;

  const submit = async () => {
    setTouched(true);
    if (invalid || submitting) return;
    await onConfirm({
      justification: justification.trim(),
      ...(medecin.trim() ? { medecin_ref: medecin.trim() } : {}),
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissable={!submitting}
      title={t("validation.breakglass.title")}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            {t("validation.cancel")}
          </Button>
          <Button variant="danger" onClick={() => void submit()} loading={submitting === true} disabled={invalid}>
            {t("validation.breakglass.submit")}
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: token.s4 }}>
        <p style={{ margin: 0, color: token.slate, fontSize: token.fsLabel }}>
          {t("validation.breakglass.desc")}
        </p>
        <FormField
          label={t("validation.breakglass.justification")}
          hint={t("validation.breakglass.justification.hint")}
          required
          error={touched && invalid ? t("validation.breakglass.required") : undefined}
        >
          {(p) => (
            <Textarea
              {...p}
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              invalid={touched && invalid}
              rows={3}
            />
          )}
        </FormField>
        <FormField label={t("validation.breakglass.medecin")} hint={t("validation.breakglass.medecin.hint")}>
          {(p) => (
            <Input
              {...p}
              mono
              value={medecin}
              onChange={(e) => setMedecin(e.target.value)}
              placeholder={t("validation.id.placeholder")}
            />
          )}
        </FormField>
        <p
          role="note"
          style={{
            margin: 0,
            padding: token.s3,
            borderRadius: token.rSm,
            border: `1px solid ${token.warn}`,
            background: token.tintHigh,
            color: token.ink,
            fontSize: token.fsLabel,
          }}
        >
          <span aria-hidden>▲ </span>
          {t("validation.breakglass.scope")}
        </p>
      </div>
    </Modal>
  );
}
